export function selectedProductIds(selectedProducts) {
  return (selectedProducts || [])
    .map((product) => product?.id)
    .filter(Boolean)
    .map(String);
}

export function productIdsForPriceHistoryFilters(selectedProducts) {
  const ids = selectedProductIds(selectedProducts);
  return ids.length > 0 ? ids : undefined;
}

export function soleSelectedProduct(selectedProducts) {
  if (!Array.isArray(selectedProducts) || selectedProducts.length !== 1) {
    return null;
  }
  return selectedProducts[0];
}

/**
 * Chart title/series label for the current Products filter.
 * One product keeps its name; two or more are treated as one grouped series.
 */
export function formatSelectedProductsChartLabel(selectedProducts, formatProductOptionLabel) {
  if (!selectedProducts?.length) {
    return "";
  }
  if (selectedProducts.length === 1) {
    return formatProductOptionLabel(selectedProducts[0]);
  }
  return "Selected products";
}

export function retailersForPriceHistoryFilters(selectedRetailers) {
  const names = selectedRetailerNames(selectedRetailers);
  return names.length > 0 ? names : undefined;
}

export function selectedRetailerNames(selectedRetailers) {
  return (selectedRetailers || [])
    .map((retailer) => (typeof retailer === "string" ? retailer : retailer?.name))
    .filter(Boolean)
    .map(String);
}

export function formatSelectedRetailersPrintLabel(selectedRetailers) {
  const names = selectedRetailerNames(selectedRetailers);
  if (!names.length) {
    return "All retailers";
  }
  if (names.length === 1) {
    return names[0];
  }
  return names.join(", ");
}

export function formatPriceHistoryFilterCount(count) {
  if (count == null || count <= 0) return null;
  return Number(count).toLocaleString();
}

/**
 * @param {any[]} aggregateRows
 * @param {string} fieldName
 * @returns {Map<string, number>}
 */
export function priceHistoryCountByFieldFromAggregateRows(aggregateRows, fieldName) {
  const countByFieldValue = new Map();
  for (const row of aggregateRows || []) {
    const rawValue = row?.[fieldName];
    if (rawValue == null || !String(rawValue).trim()) continue;
    const fieldValue = String(rawValue).trim();
    const count = Number(row?.count ?? 0);
    countByFieldValue.set(fieldValue, Number.isFinite(count) ? count : 0);
  }
  return countByFieldValue;
}

/**
 * @param {any[]} aggregateRows
 * @param {(row: any) => string} normalizeProductId
 * @returns {Map<string, number>}
 */
export function productPriceHistoryCountByIdFromAggregateRows(
  aggregateRows,
  normalizeProductId
) {
  const countByProductId = new Map();
  for (const row of aggregateRows || []) {
    const productId = normalizeProductId(row);
    if (!productId) continue;
    const count = Number(row?.count ?? 0);
    countByProductId.set(productId, Number.isFinite(count) ? count : 0);
  }
  return countByProductId;
}

export function retailerPriceHistoryCountByNameFromAggregateRows(aggregateRows) {
  return priceHistoryCountByFieldFromAggregateRows(aggregateRows, "retailer");
}

export function retailerOptionFromName(name, priceHistoryCount = 0) {
  return {
    name: String(name),
    priceHistoryCount: Number.isFinite(Number(priceHistoryCount)) ? Number(priceHistoryCount) : 0,
  };
}

export function buildRetailerOptionsWithCurrentName(retailerChoices, currentName) {
  const options = [...(retailerChoices || [])];
  const trimmed = String(currentName || "").trim();
  if (trimmed && !options.some((option) => option?.name === trimmed)) {
    options.push(retailerOptionFromName(trimmed, 0));
    options.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }
  return options;
}

export function findRetailerOptionByName(options, name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) {
    return null;
  }
  return (
    (options || []).find((option) => option?.name === trimmed) ?? retailerOptionFromName(trimmed, 0)
  );
}

export function formatSelectedProductsPrintLabel(selectedProducts, formatProductOptionLabel) {
  if (!selectedProducts?.length) {
    return "All products";
  }
  if (selectedProducts.length === 1) {
    return formatProductOptionLabel(selectedProducts[0]);
  }
  return selectedProducts.map((product) => formatProductOptionLabel(product)).join(", ");
}
